import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Divider,
  Grid,
  TextField,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  Tooltip,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  consultaPaciente,
  creaPaciente,
  actualizaPaciente,
  getListFinanciadores,
  integraBoton,
} from "../../../services/MyPatientsServices";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormHelperText from "@mui/material/FormHelperText";
import { useTheme } from "@mui/material/styles";
import listTipoDocumento from "../../../constants/tipoDocumento";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { enqueueSnackbar } from "notistack";
import { GenerateRecipeButton } from "../../GenerateRecipeButton";
import listSexo from "../../../constants/sexo";
import { getDecryptedDataFromSessionStorage } from "../../../utils";
import { SECRET_KEY } from "../../../constants/encriptKey";

export const FormPatient = (props) => {
  const location = useLocation();
  const theme = useTheme();

  const { action } = props;
  const { idPaciente } = useParams() || {};
  const data = getDecryptedDataFromSessionStorage(SECRET_KEY);
  const { idMedico } = data
  const [loading, setLoading] = useState(false);
  const [loadingGenerarReceta, setLoadingGenerarReceta] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  const [patientDataToEdit, setPatientDataToEdit] = useState(null);
  const [listFinanciadores, setListFinanciadores] = useState([]);

  useEffect(() => {
    const fetchFinanciadores = async () => {
      try {
        const financiadoresData = await getListFinanciadores();
        setListFinanciadores(financiadoresData.results);
      } catch (error) {
        console.error("Error al obtener la lista de financiadores:", error);
      }
    };

    fetchFinanciadores();
  }, []);

  const formik = useFormik({
    initialValues: {
      idMedico,
      nombre: "",
      apellido: "",
      tipoDocumento: "",
      nroDocumento: "",
      fechaNacimiento: "",
      email: "",
      telefono: "",
      sexo: "",
      financiadores: [
        {
          nombreFinanciador: "",
          nroFinanciador: "",
          nroAfiliado: "",
          plan: "",
        },
      ],
      action: action,
    },
    validationSchema: Yup.object({
      nombre: Yup.string().required("El nombre es requerido"),
      apellido: Yup.string().required("El apellido es requerido"),
      tipoDocumento: Yup.string().required("El tipo de documento es requerido"),
      nroDocumento: Yup.string()
        .max(12, "El número de documento debe contener máximo 12 dígitos")
        .required("El número de documento es requerido")
        .matches(
          /^[0-9]+$/,
          "El número de documento debe contener solo números"
        ),
      fechaNacimiento: Yup.string()
        .required("La fecha de nacimiento es requerida")
        .test(
          "is-before-today",
          "La fecha de nacimiento debe ser anterior al día de hoy",
          (value) => {
            const selectedDate = new Date(value);
            const today = new Date();
            return selectedDate < today;
          }
        ),
      email: Yup.string()
        .email("Ingrese un correo electrónico válido")
        .required("El correo electrónico es requerido")
        .max(100, "El campo Email puede tener hasta 100 caracteres"),
      telefono: Yup.string().when("action", {
        is: (val) => val === "update",
        then: Yup.string()
          .required("El teléfono es requerido")
          .matches(/^[0-9]+$/, "El teléfono debe contener solo números"),
        otherwise: Yup.string()
          .required("El teléfono es requerido")
          .matches(/^[0-9]+$/, "El teléfono debe contener solo números"),
      }),
      sexo: Yup.string().required("El sexo es requerido"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await handleSubmit();
        if (action === "new") {
          formik.resetForm();
          setFinanciadores([]);
        }
      } catch (error) {
        console.error("Error al enviar el formulario:", error);
        setErrorMessage("Error al enviar el formulario");
      } finally {
        setLoading(false);
      }
    },
  });

  const validateNroAfiliado = (value) => {
    let errorMessage = "";
    if (!/^[0-9]*$/.test(value)) {
      errorMessage = "El Nro de Afiliado debe contener solo números";
    } else if (value.length > 25) {
      errorMessage = "El Nro de Afiliado debe contener como máximo 25 dígitos";
    }
    return errorMessage;
  };

  const formatDateForInput = (utcDate) => {
    const date = new Date(utcDate);
    const formattedDate = date.toISOString().split("T")[0];
    return formattedDate;
  };

  useEffect(() => {
    const loadPatientData = async () => {
      try {
        const res = await consultaPaciente(idPaciente, idMedico);
        const formattedDate = formatDateForInput(res.result.fechaNacimiento);

        formik.setValues({
          ...res.result,
          fechaNacimiento: formattedDate,
        });
        setFinanciadores(res.result.financiadores || []);
      } catch (error) {
        console.error("Error al cargar datos del paciente:", error);
      }
    };

    if (action === "new") {
      formik.setValues({
        idMedico,
        nombre: "",
        apellido: "",
        tipoDocumento: "",
        nroDocumento: "",
        fechaNacimiento: "",
        email: "",
        telefono: "",
        sexo: "",
        financiadores: [
          {
            nombreFinanciador: "",
            nroFinanciador: "",
            nroAfiliado: "",
            plan: "",
          },
        ],
      });
    } else {
      loadPatientData();
    }
  }, [idPaciente, idMedico, action]);

  useEffect(() => {
    if (patientDataToEdit && patientDataToEdit.financiadores) {
      setFinanciadores(patientDataToEdit.financiadores);
    }
  }, [patientDataToEdit]);

  const [disableCreatePatientButton, setDisableCreatePatientButton] =
    useState(true);
  const [financiadores, setFinanciadores] = useState([]);
  const [newFinanciador, setNewFinanciador] = useState({
    nombreFinanciador: "",
    nroFinanciador: "",
    nroAfiliado: "",
    plan: "",
  });

  useEffect(() => {
    const loadPatientData = async () => {
      try {
        const result = await consultaPaciente(idPaciente, idMedico);
        setPatientDataToEdit(result);
      } catch (error) {
        console.error("Error al cargar datos del paciente:", error);
      }
    };

    if (action === "edit") {
      loadPatientData();
    }
  }, [idPaciente, idMedico, action]);

  useEffect(() => {
    if (patientDataToEdit) {
      setFinanciadores(patientDataToEdit.financiadores || []);
    }
  }, [patientDataToEdit]);

  useEffect(() => {
    const conditionToDisableButton = financiadores.length === 0;
    setDisableCreatePatientButton(conditionToDisableButton);
  }, [financiadores]);

  const handleAddFinanciador = () => {
    const validationNroAfiliadoType = /^[0-9]*$/.test(
      newFinanciador.nroAfiliado
    );

    if (
      newFinanciador.nombreFinanciador &&
      newFinanciador.nroFinanciador &&
      newFinanciador.plan &&
      newFinanciador.nroAfiliado.toString().length < 25 &&
      validationNroAfiliadoType
    ) {
      setFinanciadores([...financiadores, newFinanciador]);
      setNewFinanciador({
        nombreFinanciador: "",
        nroFinanciador: "",
        nroAfiliado: "",
        plan: "",
      });
    }
  };
  const handleFinanciadorChange = (e) => {
    const financiadorId = e.target.value;
    const selectedFinanciador = listFinanciadores.find(
      (item) => item.nroFinanciador === financiadorId
    );
    setNewFinanciador({
      nombreFinanciador: selectedFinanciador?.nombreFinanciador || "",
      nroFinanciador: financiadorId,
      nroAfiliado: "",
      plan: "",
    });
  };

  const handleDeleteFinanciador = (nroFinanciador) => {
    const newFinanciadores = financiadores.filter(
      (_, i) => i !== nroFinanciador
    );
    setFinanciadores(newFinanciadores);
  };

  const handleChangeLimit = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const startIndex = page * limit;
  const endIndex = startIndex + limit;
  const currentItems = financiadores.slice(startIndex, endIndex);

  const handlePatientFormChange = (e) => {
    formik.handleChange(e);
  };

  const handleSubmit = async () => {
    try {
      const pacienteConFinanciadores = {
        ...formik.values,
        financiadores,
      };

      if (action === "update") {
        await actualizaPaciente(pacienteConFinanciadores);
      } else {
        await creaPaciente(pacienteConFinanciadores);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
  };

  const handleGenerarReceta = async (patient) => {
    const currentLocation = location.pathname;

    const data = {
      idMedico: idMedico,
      nombre: patient.nombre,
      apellido: patient.apellido,
      tipoDocumento: patient.tipoDocumento,
      nroDocumento: patient.nroDocumento,
      fechaNacimiento: patient.fechaNacimiento,
      email: patient.email,
      telefono: patient.telefono,
      sexo: patient.sexo,
      nombreFinanciador: patient.nombreFinanciador,
      nroFinanciador: patient.nroFinanciador,
      nroAfiliado: patient.nroAfiliado,
      plan: patient.plan,
      urlCallback: currentLocation,
    };
    setLoadingGenerarReceta(true);
    try {
      const response = await integraBoton(data);

      if (response.statusResult === 200) {
        //window.open(response.result.url, "_blank");
        window.location.href = response.result.url;
        setLoadingGenerarReceta(false);
      } else {
        if (Array.isArray(response.errors) && response.errors.length > 0) {
          enqueueSnackbar(response.errors[0], { variant: "error" });
        }
        setLoadingGenerarReceta(false);
      }
    } catch (error) {
      console.error("Error en el servicio integraBoton:", error);
      enqueueSnackbar("Ocurrió un error en el servicio", { variant: "error" });
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* Card de paciente */}
      <Card
        style={{
          border: "1px solid #ddd",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          borderRadius: "8px",
        }}
      >
        <CardHeader
          title="Formulario de paciente"
          subheader={`${
            action === "new"
              ? "Crear nuevo paciente"
              : "Editar Información del paciente"
          }`}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                variant="outlined"
                onChange={handlePatientFormChange}
                value={formik.values.nombre}
                {...formik.getFieldProps("nombre")}
                error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                helperText={formik.touched.nombre && formik.errors.nombre}
                disabled={action !== "new" ? true : false}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                label="Apellido"
                name="apellido"
                variant="outlined"
                onChange={handlePatientFormChange}
                value={formik.values.apellido}
                {...formik.getFieldProps("apellido")}
                error={
                  formik.touched.apellido && Boolean(formik.errors.apellido)
                }
                helperText={formik.touched.apellido && formik.errors.apellido}
                disabled={action !== "new" ? true : false}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                variant="outlined"
                onChange={handlePatientFormChange}
                value={formik.values.email}
                {...formik.getFieldProps("email")}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={action === "view"}
              />
            </Grid>

            <Grid item md={4} xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ my: 0, marginBottom: 2 }}
              >
                <TextField
                  select
                  label="Tipo de Documento"
                  name="tipoDocumento"
                  sx={{
                    borderColor: formik.touched.tipoDocumento
                      ? Boolean(formik.errors.tipoDocumento)
                        ? theme.palette.error.main
                        : theme.palette.primary.main
                      : "rgba(0, 0, 0, 0.23)",
                    color: "text.primary",
                  }}
                  onChange={(e) => {
                    handlePatientFormChange(e);
                    formik.setFieldTouched("tipoDocumento", false);
                  }}
                  value={formik.values.tipoDocumento}
                  error={
                    formik.touched.tipoDocumento &&
                    Boolean(formik.errors.tipoDocumento)
                  }
                  onBlur={formik.handleBlur}
                  disabled={action !== "new" ? true : false}
                >
                  <MenuItem value=""></MenuItem>
                  {listTipoDocumento.map((item) => (
                    <MenuItem key={item.valor} value={item.valor}>
                      {item.valor}
                    </MenuItem>
                  ))}
                </TextField>
                <FormHelperText sx={{ color: theme.palette.error.main }}>
                  {formik.touched.tipoDocumento && formik.errors.tipoDocumento}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                label="Nro de documento"
                name="nroDocumento"
                variant="outlined"
                onChange={handlePatientFormChange}
                value={formik.values.nroDocumento}
                {...formik.getFieldProps("nroDocumento")}
                error={
                  formik.touched.nroDocumento &&
                  Boolean(formik.errors.nroDocumento)
                }
                helperText={
                  formik.touched.nroDocumento && formik.errors.nroDocumento
                }
                disabled={action !== "new" ? true : false}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                label="Teléfono"
                name="telefono"
                variant="outlined"
                onChange={handlePatientFormChange}
                value={formik.values.telefono}
                {...formik.getFieldProps("telefono")}
                error={
                  formik.touched.telefono && Boolean(formik.errors.telefono)
                }
                helperText={formik.touched.telefono && formik.errors.telefono}
                disabled={action === "view"}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                label="Fecha de nacimiento"
                name="fechaNacimiento"
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
                onChange={handlePatientFormChange}
                value={formik.values.fechaNacimiento}
                {...formik.getFieldProps("fechaNacimiento")}
                error={
                  formik.touched.fechaNacimiento &&
                  Boolean(formik.errors.fechaNacimiento)
                }
                helperText={
                  formik.touched.fechaNacimiento &&
                  formik.errors.fechaNacimiento
                }
                disabled={action !== "new" ? true : false}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ my: 0, marginBottom: 2 }}
              >
                <TextField
                  select
                  label="Sexo"
                  name="sexo"
                  sx={{
                    borderColor: formik.touched.sexo
                      ? Boolean(formik.errors.sexo)
                        ? theme.palette.error.main
                        : theme.palette.primary.main
                      : "rgba(0, 0, 0, 0.23)",
                    color: "text.primary",
                  }}
                  onChange={(e) => {
                    handlePatientFormChange(e);
                    formik.setFieldTouched("sexo", false);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.sexo}
                  error={formik.touched.sexo && Boolean(formik.errors.sexo)}
                  disabled={action !== "new" ? true : false}
                >
                  <MenuItem value=""></MenuItem>
                  {listSexo.map((item) => (
                    <MenuItem key={item.valor} value={item.valor}>
                      {item.valor}
                    </MenuItem>
                  ))}
                </TextField>
                <FormHelperText sx={{ color: theme.palette.error.main }}>
                  {formik.touched.sexo && formik.errors.sexo}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          {/* Card de financiadores */}
          <Card
            style={{
              border: "1px solid #ddd",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              marginTop: "10px",
            }}
          >
            {action !== "view" && (
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item md={3} xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel
                        htmlFor="financiador"
                        sx={{ color: "text.primary" }}
                      >
                        Financiador
                      </InputLabel>
                      <Select
                        native
                        label="Financiador"
                        inputProps={{
                          name: "financiador",
                          id: "financiador",
                        }}
                        sx={{ color: "text.primary" }}
                        onChange={handleFinanciadorChange}
                        value={newFinanciador.nroFinanciador}
                      >
                        <option value=""></option>
                        {listFinanciadores.map((item) => (
                          <option
                            key={item.nroFinanciador}
                            value={item.nroFinanciador}
                          >
                            {item.nombreFinanciador}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      fullWidth
                      label="Plan"
                      name="plan"
                      variant="outlined"
                      value={newFinanciador.plan}
                      onChange={(e) =>
                        setNewFinanciador({
                          ...newFinanciador,
                          plan: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      fullWidth
                      label="Nro Afiliado"
                      name="nroAfiliado"
                      variant="outlined"
                      value={newFinanciador.nroAfiliado}
                      onChange={(e) =>
                        setNewFinanciador({
                          ...newFinanciador,
                          nroAfiliado: e.target.value,
                        })
                      }
                      error={Boolean(
                        validateNroAfiliado(newFinanciador.nroAfiliado)
                      )}
                      helperText={validateNroAfiliado(
                        newFinanciador.nroAfiliado
                      )}
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <Tooltip title="Agregar financiador">
                      <IconButton
                        color="primary"
                        sx={{
                          marginRight: "8px",
                        }}
                        disabled={
                          !newFinanciador.nombreFinanciador ||
                          !newFinanciador.plan ||
                          !newFinanciador.nroAfiliado
                        }
                        onClick={handleAddFinanciador}
                      >
                        <AddCircleIcon fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </CardContent>
            )}
            <Divider />
            <CardActions>
              <Card
                sx={{
                  border: "0.5px solid #dddddd",
                  padding: "1px",
                  boxShadow: "1px 1px #d5d5d5",
                  margin: "auto",
                }}
              >
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Table sx={{ minWidth: 1050 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Financiador</TableCell>
                          <TableCell>Plan</TableCell>
                          <TableCell>Nro de afiliado</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              Acciones
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {currentItems.map((financiador, index) => (
                          <TableRow hover key={index}>
                            <TableCell>
                              {financiador.nombreFinanciador}
                            </TableCell>
                            <TableCell>{financiador.plan}</TableCell>
                            <TableCell>{financiador.nroAfiliado}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  alignItems: "center",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                {action !== "view" && (
                                  <Tooltip title="Borrar financiador">
                                    <IconButton
                                      sx={{
                                        marginRight: "8px",
                                      }}
                                      color="error"
                                      onClick={() =>
                                        handleDeleteFinanciador(index)
                                      }
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {action === "view" &&
                                  (loadingGenerarReceta ? (
                                    <CircularProgress
                                      color="primary"
                                      size={20}
                                      sx={{
                                        marginLeft: "8px",
                                      }}
                                    />
                                  ) : (
                                    <GenerateRecipeButton
                                      onClick={() => {
                                        const patient = {
                                          nombre: formik.values.nombre,
                                          apellido: formik.values.apellido,
                                          tipoDocumento:
                                            formik.values.tipoDocumento,
                                          nroDocumento:
                                            formik.values.nroDocumento,
                                          fechaNacimiento:
                                            formik.values.fechaNacimiento,
                                          email: formik.values.email,
                                          telefono: formik.values.telefono,
                                          sexo: formik.values.sexo,
                                          nombreFinanciador:
                                            financiador.nombreFinanciador,
                                          nroFinanciador:
                                            financiador.nroFinanciador,
                                          nroAfiliado: financiador.nroAfiliado,
                                          plan: financiador.plan,
                                          urlCallback: "recetaonline.ar",
                                        };
                                        handleGenerarReceta(patient);
                                      }}
                                    />
                                  ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!financiadores || financiadores.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              <Alert severity="info">
                                No hay financiadores para mostrar
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                <Box
                  sx={{
                    alignItems: "center",
                    padding: "10px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {!!loading && (
                    <div>
                      <CircularProgress />
                    </div>
                  )}
                </Box>
                <TablePagination
                  component="div"
                  count={(financiadores && financiadores.length) || 0}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeLimit}
                  page={page}
                  rowsPerPage={limit}
                  labelRowsPerPage="Filas por página"
                  rowsPerPageOptions={[5, 10, 20]}
                />
              </Card>
            </CardActions>
          </Card>
        </CardContent>
        <Divider />
        {action !== "view" && (
          <CardActions sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
              }}
            >
              {errorMessage && (
                <Stack sx={{ width: "auto", ml: "auto" }} spacing={2}>
                  <Alert fullWidth severity="error">
                    {errorMessage}
                  </Alert>
                </Stack>
              )}
              <LoadingButton
                color="primary"
                size="large"
                type="submit"
                variant="contained"
                sx={{ ml: 2 }}
                disabled={disableCreatePatientButton}
              >
                {action === "update" ? "Actualizar paciente" : "Crear paciente"}
              </LoadingButton>
            </Box>
          </CardActions>
        )}
      </Card>
    </form>
  );
};
